// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The viewfinder.
 *
 * The stream is held only for as long as the viewfinder is open — `stop()` runs
 * on capture, on cancel, on Escape and on page hide, so the camera indicator
 * never stays lit after the user has moved on.
 */

// `torch` is a well-supported constraint that hasn't made it into lib.dom yet.
interface TorchCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}
interface TorchConstraints extends MediaTrackConstraintSet {
  torch?: boolean;
}

export function isCameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    // A camera on an insecure origin will always fail; don't offer the button.
    (window.isSecureContext ?? false)
  );
}

export class Camera {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;

  get isOpen(): boolean {
    return this.stream !== null;
  }

  /** Opens the rear camera at the highest resolution the device will give us. */
  async start(video: HTMLVideoElement): Promise<void> {
    this.stop();

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 3840 },
        height: { ideal: 2160 },
      },
      audio: false,
    });

    this.stream = stream;
    this.video = video;
    video.srcObject = stream;

    await video.play().catch(() => {
      // Autoplay can be refused; the stream is still live and the user can tap.
    });
  }

  stop(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    if (this.video) {
      this.video.srcObject = null;
      this.video = null;
    }
  }

  private get track(): MediaStreamTrack | null {
    return this.stream?.getVideoTracks()[0] ?? null;
  }

  get hasTorch(): boolean {
    const track = this.track;
    if (!track || typeof track.getCapabilities !== 'function') return false;
    return Boolean((track.getCapabilities() as TorchCapabilities).torch);
  }

  async setTorch(on: boolean): Promise<void> {
    const track = this.track;
    if (!track) return;
    await track.applyConstraints({ advanced: [{ torch: on } as TorchConstraints] });
  }

  /**
   * Grabs the current frame at the sensor's own resolution — not the size the
   * `<video>` happens to be laid out at, which on a phone is a fraction of it.
   */
  capture(): HTMLCanvasElement | null {
    const video = this.video;
    if (!video || !video.videoWidth || !video.videoHeight) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(video, 0, 0);
    return canvas;
  }
}
